"use client";

import { Alert } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Icon } from "@/components/ui/icon";
import { Modal, ModalBody, ModalContent, ModalFooter, ModalHeader } from "@/components/ui/modal";
import { Paper } from "@/components/ui/paper";
import { SkeletonBox, SkeletonLabel } from "@/components/ui/skeleton";

import { useScorecardLibrary } from "../hooks/use-library";
import { fieldCountLabel } from "./shared";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  takenNames: string[];
  onPick: (libraryDefinitionId: string) => Promise<void>;
  busy?: boolean;
}

export function LibraryPicker({ open, onOpenChange, takenNames, onPick, busy }: Props) {
  const lib = useScorecardLibrary(false);
  const taken = new Set(takenNames.map((n) => n.toLowerCase()));
  const items = lib.data ?? [];

  return (
    <Modal open={open} onOpenChange={onOpenChange}>
      <ModalContent size="md">
        <ModalHeader title="Add from library" iconName="IconClipboard" />
        <ModalBody className="flex flex-col gap-2 p-5">
          {lib.isLoading && !lib.data && (
            <>
              {Array.from({ length: 3 }).map((_, i) => (
                <Paper.Container key={i}>
                  <Paper.Body>
                    <SkeletonLabel className="w-40" />
                    <SkeletonLabel tone="muted" className="w-24" />
                  </Paper.Body>
                  <SkeletonBox tone="muted" className="h-7 w-14 rounded-sm" />
                </Paper.Container>
              ))}
            </>
          )}

          {lib.isError && !lib.data && (
            <Alert.Container variant="destructive">
              <Alert.Icon>
                <Icon name="IconExclamationCircle" size={16} aria-hidden="true" />
              </Alert.Icon>
              <Alert.Content>Could not load the library.</Alert.Content>
            </Alert.Container>
          )}

          {lib.data && items.length === 0 && (
            <div className="flex flex-col items-center gap-1.5 rounded-lg border border-border bg-surface-inset px-3 py-10 text-center">
              <Icon name="IconListSparkle" size={22} className="text-heading opacity-30" aria-hidden="true" />
              <p className="text-sm font-medium text-heading">The library is empty</p>
              <p className="text-[13px] text-contrast/50">
                Create types under Settings › Scorecards.
              </p>
            </div>
          )}

          {items.map((d) => {
            const isTaken = taken.has(d.name.toLowerCase());
            return (
              <Paper.Container key={d.id} className="gap-3">
                <Paper.Body>
                  <Paper.TitleRow>
                    <Icon
                      name="IconChecklist"
                      size={14}
                      className="shrink-0 text-muted-foreground"
                      aria-hidden="true"
                    />
                    <Paper.Title>{d.name}</Paper.Title>
                    {d.is_default && (
                      <Badge variant="outline-muted" size="xs" className="rounded-sm">
                        Default set
                      </Badge>
                    )}
                    {isTaken && (
                      <Badge variant="success" size="xs" className="rounded-sm">
                        On this job
                      </Badge>
                    )}
                  </Paper.TitleRow>
                  <Paper.Description>
                    {fieldCountLabel(d.current_version.fields.length)}
                  </Paper.Description>
                </Paper.Body>
                <Button
                  variant="secondary"
                  size="sm"
                  disabled={busy || isTaken}
                  title={isTaken ? "Already on this job" : undefined}
                  onClick={() => void onPick(d.id)}
                  icon={isTaken ? undefined : <Icon name="IconPlusSmall" size={15} />}
                >
                  {isTaken ? "Added" : "Add"}
                </Button>
              </Paper.Container>
            );
          })}
        </ModalBody>
        <ModalFooter className="px-5">
          <Button variant="secondary" size="sm" onClick={() => onOpenChange(false)}>
            Close
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
